import { ActionIcon, Menu, Table } from '@mantine/core';
import { IconDots, IconSend, IconTrash } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useRouter } from '@tanstack/react-router';
import { api } from '@/clients/admin.client';
import { StatusIcon } from '../../../../../components/StatusIcon';
import { notifyError, notifySuccess } from '@/lib/notification';
import { logger } from '@/lib/logger';
import { Invitation } from '@/clients/types';

type Props = {
  invitations: Invitation[];
  adminClient: typeof api;
};

const isExpired = (invitation: Invitation) => new Date() > new Date(invitation.expires_at);

export function InvitationList({ invitations, adminClient }: Props) {
  const router = useRouter();

  // handlers
  const handleRevokeInvitation = async (invitation: Invitation) => {
    try {
      await adminClient.admin_v1_delete_invitation(undefined, {
        params: { invitation_id: invitation.id },
      });
      await router.load();
      notifySuccess(`${invitation.invitee} への招待を取り消しました。`);
    } catch (e) {
      logger.error(e);
      notifyError(`${invitation.invitee} への招待の取り消しに失敗しました。`);
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    try {
      await adminClient.admin_v1_invite_user({
        email: invitation.invitee,
        role: invitation.role as 'admin' | 'user',
      });
      await router.load();
      notifySuccess(`${invitation.invitee} への招待を再送しました。`);
    } catch (e) {
      logger.error(e);
      notifyError(`${invitation.invitee} への招待の再送に失敗しました。`);
    }
  };

  // modals
  const openConfirmRevokeInvitation = (invitation: Invitation) => {
    modals.openConfirmModal({
      title: '招待を取り消しても良いですか？',
      children: (
        <div className="text-sm">
          この操作により、 <strong>{invitation.invitee}</strong> の招待リンクが無効になります。
        </div>
      ),
      onConfirm: () => handleRevokeInvitation(invitation),
    });
  };

  return (
    <Table.ScrollContainer minWidth={800} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>メール</Table.Th>
            <Table.Th>ステータス</Table.Th>
            <Table.Th>権限</Table.Th>
            <Table.Th>招待日 (有効期限)</Table.Th>
            <Table.Th>招待者</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {invitations.map((invitation) => (
            <Table.Tr key={invitation.id}>
              <Table.Td data-label="メール">{invitation.invitee}</Table.Td>
              <Table.Td data-label="ステータス">
                {isExpired(invitation) ? (
                  <div className="flex items-center">
                    <StatusIcon color="red" />
                    期限切れ
                  </div>
                ) : (
                  <div className="flex items-center">
                    <StatusIcon color="green" />
                    承認待ち
                  </div>
                )}
              </Table.Td>
              <Table.Td data-label="権限">
                {invitation.role === 'admin' ? 'テナント管理者' : '一般ユーザ'}
              </Table.Td>
              <Table.Td data-label="招待日 (有効期限)">
                {/* カード化では td 直下の子が横に並ぶので、値は 1 つの要素にまとめる */}
                <div>
                  <p>{invitation.created_at}</p>
                  <p>({invitation.expires_at})</p>
                </div>
              </Table.Td>
              <Table.Td data-label="招待者">{invitation.inviter}</Table.Td>
              <Table.Td data-action>
                <Menu>
                  <Menu.Target>
                    <ActionIcon variant="outline" size="md">
                      <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconSend style={{ width: '70%', height: '70%' }} />}
                      onClick={() => handleResendInvitation(invitation)}
                      disabled={!isExpired(invitation)}
                    >
                      再送
                    </Menu.Item>
                    <Menu.Item
                      leftSection={
                        <IconTrash
                          style={{ width: '70%', height: '70%' }}
                          className="text-plamo-error"
                        />
                      }
                      onClick={() => openConfirmRevokeInvitation(invitation)}
                    >
                      <div className="text-plamo-error">招待を取り消す</div>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
